import {
  Group,
  Material,
  MathUtils,
  Mesh,
  Object3D,
  ObjectLoader,
  Vector3,
} from 'three';
import * as TWEEN from '@tweenjs/tween.js';

import { parseDirectObjectModelJSON } from './direct-object-model-json';
import { isDirectOpaqueId } from './direct-scene-profile';

export const DIRECT_OBJECT_MAX_COUNT = 128;

/** Đáy khối cách sàn bao nhiêu mm khi Leanbot gắp mà không nói rõ độ cao. */
export const DIRECT_OBJECT_GRIP_DEFAULT_Z_MM = 5;

/**
 * Trần độ cao nâng khối. Tay gắp Leanbot chỉ với tới cỡ vài chục mm, nên một
 * giá trị lớn hơn thế gần như chắc chắn là nhầm đơn vị (mét thay vì mm), mà
 * hậu quả là khối bay lơ lửng ngoài khung hình suốt phần còn lại của bài.
 */
export const DIRECT_OBJECT_MAX_Z_MM = 200;

/**
 * Điểm gắp dự phòng trong hệ toạ độ gốc robot, suy từ hình học createbot.mjs:
 * trục hai servo nằm cách gốc robot 70 mm về phía trước và đối xứng qua trục
 * dọc, nên điểm giữa chúng có z = 0. Chỉ dùng khi không tra được mesh shaft,
 * tức robot chưa dựng xong, vì bot được add bất đồng bộ qua roomInit.
 */
const FALLBACK_GRIP_OFFSET_X_MM = 70;

/**
 * Tên mesh trục servo trong robot, do createbot.mjs đặt (buildServo đặt tên
 * "shaft" cho trục, buildFront đặt tên hai servo). Khai lại ở đây thay vì import
 * từ physical-model để lớp này không kéo theo cả module robot, nó chỉ cần tra
 * hai cái tên trong cây scene.
 */
const SERVO_NAMES = ['servo-left', 'servo-right'] as const;
const SERVO_SHAFT_NAME = 'shaft';

/**
 * Trần thời gian tween của một lệnh move. Một lệnh dài hơn thế gần như chắc
 * chắn là lỗi đơn vị (nhầm mili-giây với giây), mà hậu quả là khối treo lơ
 * lửng giữa đường suốt phần còn lại của bài, nên chặn ngay ở biên.
 */
export const DIRECT_OBJECT_MOVE_MAX_DURATION_S = 60;

export interface DirectObjectPose {
  xMm: number;
  yMm: number;
  headingDeg: number;
  /** Đáy khối cách sàn bao nhiêu mm. Vắng mặt = đặt trên sàn. */
  zMm?: number;
}

export function isDirectObjectId(value: unknown): value is string {
  return isDirectOpaqueId(value);
}

function isDirectObjectPose(value: DirectObjectPose): boolean {
  if (
    !value ||
    !Number.isFinite(value.xMm) ||
    !Number.isFinite(value.yMm) ||
    !Number.isFinite(value.headingDeg)
  ) return false;
  // zMm là khoá tuỳ chọn, nhưng có mặt thì phải hợp lệ chứ không được lọt qua
  // như thể không có.
  if (value.zMm === undefined) return true;
  return (
    Number.isFinite(value.zMm) &&
    value.zMm >= 0 &&
    value.zMm <= DIRECT_OBJECT_MAX_Z_MM
  );
}

/** `null` = lệnh move không hợp lệ; số = số giây tween đã kiểm tra. */
function moveDurationSeconds(value: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value < 0 || value > DIRECT_OBJECT_MOVE_MAX_DURATION_S) return null;
  return value;
}

function disposeObjectResources(root: Object3D) {
  const geometries = new Set<Mesh['geometry']>();
  const materials = new Set<Material>();

  root.traverse(object => {
    if (!(object instanceof Mesh)) return;
    geometries.add(object.geometry);
    const objectMaterials = Array.isArray(object.material)
      ? object.material
      : [object.material];
    objectMaterials.forEach(material => materials.add(material));
  });

  geometries.forEach(geometry => geometry.dispose());
  materials.forEach(material => material.dispose());
}

/** A mission-neutral layer for validated ObjectLoader models keyed by ID. */
export class DirectObjectLayer extends Group {
  private readonly objectLoader = new ObjectLoader();
  private readonly objectsById = new Map<string, Group>();
  // Nhiều nhất một tween đang chạy cho mỗi objId. Tween nằm trong group mặc
  // định của thư viện, vốn đã được TWEEN.update() trong vòng animate của
  // createbot.mjs chạy mỗi frame — chỉ cần .start() là nó tự đi.
  private readonly movesById = new Map<string, TWEEN.Tween<{ t: number }>>();
  // Robot để gắn khối vào khi Leanbot gắp. Không phải con của layer này mà là
  // anh em cùng cha (Saban), nên phải được đưa vào từ ngoài.
  private robot: Object3D | null = null;

  constructor() {
    super();
    this.name = 'DirectObjectLayer';
  }

  setRobot(robot: Object3D | null) {
    this.robot = robot;
  }

  createObject(objId: string, modelJSON: unknown) {
    if (!isDirectObjectId(objId)) return false;
    const parsedModel = parseDirectObjectModelJSON(modelJSON);
    if (!parsedModel) return false;

    let model: Object3D;
    try {
      model = this.objectLoader.parse(parsedModel);
    } catch (_error) {
      return false;
    }
    if (!(model instanceof Group)) {
      disposeObjectResources(model);
      return false;
    }

    let wrapper = this.objectsById.get(objId);
    if (!wrapper) {
      if (this.objectsById.size >= DIRECT_OBJECT_MAX_COUNT) {
        disposeObjectResources(model);
        return false;
      }
      wrapper = new Group();
      wrapper.name = `DirectObject:${objId}`;
      wrapper.visible = false;
      this.objectsById.set(objId, wrapper);
      this.add(wrapper);
    }

    const previousModels = [...wrapper.children];
    previousModels.forEach(previousModel => wrapper?.remove(previousModel));
    wrapper.add(model);
    previousModels.forEach(disposeObjectResources);
    return true;
  }

  /**
   * Cho khối đi theo Leanbot, đúng chỗ tay gắp giữ nó.
   *
   * Gắn thẳng vào cây scene của robot thay vì chép lại tư thế robot mỗi khung
   * hình: ThreeJS tự nhân ma trận nên khối bám dính tuyệt đối, không bao giờ
   * trễ một khung so với robot dù pose tới từ tween hay từ telemetry.
   *
   * @param zMm Đáy khối cao hơn mặt sàn bao nhiêu mm.
   */
  attachObjectToRobot(objId: string, zMm: number = DIRECT_OBJECT_GRIP_DEFAULT_Z_MM) {
    if (!isDirectObjectId(objId)) return false;
    if (
      typeof zMm !== 'number'
      || !Number.isFinite(zMm)
      || zMm < 0
      || zMm > DIRECT_OBJECT_MAX_Z_MM
    ) return false;

    const robot = this.robot;
    if (!robot) return false;
    const wrapper = this.objectsById.get(objId);
    if (!wrapper) return false;

    // Khối đang được cầm thì mọi tween trên sa bàn đều vô nghĩa: nó chỉ còn đi
    // theo robot.
    this.stopMove(objId);

    const grip = this.gripOffset(robot);
    // Object3D.add() tự gỡ khỏi cha cũ, nên không cần remove trước.
    robot.add(wrapper);
    wrapper.position.set(grip.x, zMm, grip.z);
    // Toạ độ đã là hệ của robot: góc 0 nghĩa là khối quay cùng hướng robot.
    // Đặt cả ba trục chứ không riêng y: detachFromRobot() giữ cho x/z luôn
    // bằng 0, nhưng khối đang cầm mà nghiêng thì nhìn ra ngay, không đáng để
    // phụ thuộc vào một bất biến ở tận hàm khác.
    wrapper.rotation.set(0, 0, 0);
    wrapper.visible = true;
    return true;
  }

  /**
   * Điểm giữa hai tay gắp, quy về hệ toạ độ gốc robot.
   *
   * Đọc thẳng từ mesh shaft của hai servo thay vì chép sẵn một hằng số: hình
   * học robot nằm trong createbot.mjs, chép số ra đây thì mai kia ai sửa thân
   * robot là điểm gắp lệch âm thầm.
   */
  private gripOffset(robot: Object3D): { x: number; z: number } {
    const midpoint = new Vector3();
    let found = 0;

    for (const servoName of SERVO_NAMES) {
      const shaft = robot
        .getObjectByName(servoName)
        ?.getObjectByName(SERVO_SHAFT_NAME);
      if (!shaft) continue;
      shaft.updateWorldMatrix(true, false);
      midpoint.add(shaft.getWorldPosition(new Vector3()));
      found += 1;
    }

    if (found !== 2) return { x: FALLBACK_GRIP_OFFSET_X_MM, z: 0 };

    midpoint.divideScalar(found);
    robot.updateWorldMatrix(true, false);
    robot.worldToLocal(midpoint);
    return { x: midpoint.x, z: midpoint.z };
  }

  /**
   * Trả khối về lớp object sau khi Leanbot nhả nó ra. `attach` giữ nguyên vị
   * trí trong không gian thế giới, nên khối ở đúng chỗ nó đang được cầm chứ
   * không nhảy về gốc sa bàn ngay trước khi lệnh move mới đưa nó đi.
   */
  private detachFromRobot(wrapper: Group) {
    if (wrapper.parent === this) return;
    this.attach(wrapper);
    // `attach` phân rã ma trận thế giới ra quaternion, rồi ThreeJS suy
    // `rotation` thành Euler thứ tự XYZ. Với một phép quay thuần quanh trục
    // đứng góc H, phân rã đó chỉ ra (0, H, 0) khi |H| <= 90 độ; ngoài khoảng
    // ấy nó ra (180, 180 - H, 180) — cùng một hướng nhìn, nhưng rotation.y
    // không còn là heading nữa mà bị soi gương.
    //
    // Cả lớp này đọc rotation.y như heading: moveObject() lấy nó làm điểm xuất
    // phát của tween. Để nguyên thì khối nhả ở góc ngoài +-90 độ sẽ quay thừa
    // 2H - 180 độ rồi đứng lại ở hướng sai, dù Python gửi xuống đúng hướng cũ.
    // Nên quy ngay về một phép quay quanh trục đứng, ngay chỗ bất biến bị phá.
    const e = wrapper.matrix.elements;
    wrapper.rotation.set(0, Math.atan2(e[8], e[0]), 0);
  }

  /**
   * @param durationS Số giây đi từ tư thế hiện tại sang `pose`. 0 = đặt thẳng.
   *   Hàm trả về ngay trong mọi trường hợp: bên gọi (và chương trình Python
   *   sinh ra lệnh này) không chờ tween chạy xong.
   */
  moveObject(objId: string, pose: DirectObjectPose, durationS: number = 0) {
    if (!isDirectObjectId(objId) || !isDirectObjectPose(pose)) return false;
    const seconds = moveDurationSeconds(durationS);
    if (seconds === null) return false;

    const wrapper = this.objectsById.get(objId);
    if (!wrapper) return false;

    // Lệnh move mới huỷ tween cũ và đi tiếp từ chỗ khối đang đứng, không xếp
    // hàng: cảnh phải khớp với lệnh vừa nhận, không phải với lệnh trước đó.
    this.stopMove(objId);
    // Đặt khối vào một toạ độ tuyệt đối trên sa bàn cũng chính là thả nó ra.
    this.detachFromRobot(wrapper);

    const toX = pose.xMm;
    const toZ = -pose.yMm;
    // Sàn dùng quy ước Z-up (z = độ cao), ThreeJS Y-up — nên z của pose đi vào
    // trục y của scene.
    const toY = pose.zMm ?? 0;
    const toHeadingRad = MathUtils.degToRad(pose.headingDeg);

    // `visible` false nghĩa là khối chưa từng được đặt vị trí. Tween ở đây sẽ
    // thành cảnh khối trượt từ tâm sa bàn ra chỗ đứng đầu tiên, nên lần đặt
    // đầu luôn tức thời dù người dùng có xin thời gian tween.
    if (seconds === 0 || !wrapper.visible) {
      wrapper.position.set(toX, toY, toZ);
      wrapper.rotation.y = toHeadingRad;
      wrapper.visible = true;
      return true;
    }

    const fromX = wrapper.position.x;
    const fromY = wrapper.position.y;
    const fromZ = wrapper.position.z;
    const fromHeadingRad = wrapper.rotation.y;
    // Xoay theo cung ngắn nhất: từ 170 sang -170 độ là quay 20 độ, không phải
    // quay ngược 340 độ.
    const deltaHeadingRad = Math.atan2(
      Math.sin(toHeadingRad - fromHeadingRad),
      Math.cos(toHeadingRad - fromHeadingRad),
    );
    const progress = { t: 0 };

    this.movesById.set(
      objId,
      new TWEEN.Tween(progress)
        .to({ t: 1 }, seconds * 1000)
        // Khối bị robot đẩy hoặc mang đi thì chạy đều theo robot; easing vào/ra
        // sẽ làm nó trông như một vật tự tăng/giảm tốc.
        .easing(TWEEN.Easing.Linear.None)
        .onUpdate(() => {
          wrapper.position.set(
            fromX + (toX - fromX) * progress.t,
            fromY + (toY - fromY) * progress.t,
            fromZ + (toZ - fromZ) * progress.t,
          );
          wrapper.rotation.y = fromHeadingRad + deltaHeadingRad * progress.t;
        })
        .onComplete(() => {
          // Chốt đúng số đích thay vì kết quả nội suy ở t = 1, để lệnh move kế
          // tiếp xuất phát từ toạ độ sạch.
          wrapper.position.set(toX, toY, toZ);
          wrapper.rotation.y = toHeadingRad;
          this.movesById.delete(objId);
        })
        .start(),
    );
    return true;
  }

  clearObjects() {
    this.objectsById.forEach((wrapper, objId) => {
      this.stopMove(objId);
      // removeFromParent chứ không this.remove: khối đang được Leanbot cầm thì
      // cha của nó là robot, this.remove() sẽ không làm gì và để khối dính lại
      // trên robot sang tận run sau.
      wrapper.removeFromParent();
      wrapper.children.forEach(disposeObjectResources);
      wrapper.clear();
    });
    this.objectsById.clear();
  }

  /**
   * Bỏ tween đang chạy của một object. Tween sống trong group toàn cục của thư
   * viện nên nếu không gỡ, nó vẫn chạy tiếp và ghi vào một Group đã bị tháo
   * khỏi scene sau khi run kết thúc.
   */
  private stopMove(objId: string) {
    this.movesById.get(objId)?.stop();
    this.movesById.delete(objId);
  }

  dispose() {
    this.clearObjects();
    this.removeFromParent();
  }
}
